import { format, parseISO } from 'date-fns';

/**
 * Formats an ISO date string to a readable format in user's local timezone
 * @param isoDate - The ISO date string (e.g. "2025-05-05T12:25:00Z" or "2024-03-20 14:30")
 * @param formatString - Optional format string (defaults to "yyyy-MM-dd")
 * @returns The formatted date string in user's local timezone
 */
export const formatISODate = (isoDate: string, formatString: string = 'yyyy-MM-dd'): string => {
  try {
    // Parse the input date and format it
    const date = parseISO(isoDate.replace(' ', 'T'));
    return format(date, formatString);
  } catch (error) {
    console.log('Error formatting date:', error);
    return isoDate;
  }
};

/** Largest first, so the loop picks the coarsest unit that fits. */
const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 31_536_000],
  ['month', 2_592_000],
  ['week', 604_800],
  ['day', 86_400],
  ['hour', 3_600],
  ['minute', 60],
  ['second', 1],
];

/**
 * An elapsed time in words — "3 days ago", "yesterday".
 *
 * `Intl.RelativeTimeFormat` rather than date-fns, which would need a locale
 * bundle imported and mapped per language; every locale we ship comes free from
 * the platform here. `numeric: 'auto'` is what gives "yesterday" over "1 day
 * ago".
 *
 * Future timestamps are clamped to now. Server clocks run ahead of browser
 * clocks often enough that "in 2 hours" is a real outcome otherwise.
 */
export const formatRelativeTime = (isoDate: string, locale: string): string => {
  const parsed = parseISO(isoDate.replace(' ', 'T')).getTime();

  if (Number.isNaN(parsed)) {
    return '';
  }

  const seconds = Math.min(0, Math.round((parsed - Date.now()) / 1000));

  let format: Intl.RelativeTimeFormat;
  try {
    format = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  } catch {
    // Intl throws on a tag it cannot parse rather than falling back itself.
    format = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  }

  const [unit, secondsPerUnit] =
    RELATIVE_UNITS.find(([, size]) => Math.abs(seconds) >= size) ??
    RELATIVE_UNITS[RELATIVE_UNITS.length - 1];

  return format.format(Math.trunc(seconds / secondsPerUnit), unit);
};

/**
 * Returns today's date in the user's local timezone as YYYY-MM-DD.
 * Use for date validation and min/max so calendar dates are compared in local time, not UTC.
 */
export const getTodayLocalDateString = (): string => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export enum DateRange {
  Today = 'today',
  ThisWeek = 'this-week',
  LastWeek = 'last-week',
  ThisMonth = 'this-month',
  All = 'all',
}

export enum RecurrencePattern {
  Daily = 'daily',
  Weekly = 'weekly',
  Monthly = 'monthly',
}

export enum RecurrenceEndType {
  ByDate = 'by_date',
  AfterOccurrences = 'after_occurrences',
}

export const getDateForRange = (range: DateRange) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (range) {
    case DateRange.Today: {
      const endOfDay = new Date(today);
      endOfDay.setHours(23, 59, 59, 999);
      return { startDate: today.toISOString(), endDate: endOfDay.toISOString() };
    }
    case DateRange.ThisWeek: {
      const startOfWeek = new Date(today);
      const dayOfWeek = startOfWeek.getDay();
      const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Monday as start of week
      startOfWeek.setDate(startOfWeek.getDate() + diff);

      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(endOfWeek.getDate() + 6);
      endOfWeek.setHours(23, 59, 59, 999);

      return { startDate: startOfWeek.toISOString(), endDate: endOfWeek.toISOString() };
    }
    case DateRange.LastWeek: {
      const startOfLastWeek = new Date(today);
      const dayOfWeek = startOfLastWeek.getDay();
      const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      startOfLastWeek.setDate(startOfLastWeek.getDate() + diff - 7);

      const endOfLastWeek = new Date(startOfLastWeek);
      endOfLastWeek.setDate(endOfLastWeek.getDate() + 6);
      endOfLastWeek.setHours(23, 59, 59, 999);

      return { startDate: startOfLastWeek.toISOString(), endDate: endOfLastWeek.toISOString() };
    }
    case DateRange.ThisMonth: {
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);

      return { startDate: startOfMonth.toISOString(), endDate: endOfMonth.toISOString() };
    }
    default:
      return {};
  }
};
